import React from 'react';
import {StyleSheet} from 'react-native';
import Box, {IBoxProps} from '../Box/Box';
import CCColors, {updateColorWithOpacity} from '@/Utils/CCColors';
import {BorderRadius} from '@/Utils/Dimensions';

export interface CardProps extends IBoxProps {
  /**
   * Floating layers only (flyouts, popovers, modals) get the shared shadow.
   * In-flow panels stay flat - see STUDY_REDESIGN_CHECKLIST.md's elevation
   * rule. Defaults to false.
   */
  elevated?: boolean;
  /** Defaults to 12, matching the app's existing card padding convention. */
  padding?: number;
}

/**
 * Shared card surface: white background, 12px radius, hairline border.
 * There was previously no card primitive anywhere in the app - every screen
 * (including this one) hand-rolled its own card chrome inline, which is how
 * Study ended up with radii ranging from 4 to 999 and borders that drifted
 * out of sync with CCColors. Use this instead of ad hoc Box + custom styles
 * for any bounded panel/card.
 */
const Card: React.FC<CardProps> = ({
  elevated = false,
  padding = 12,
  style,
  ...rest
}) => {
  return (
    <Box
      style={[styles.base, elevated && styles.elevated, {padding}, style]}
      {...rest}
    />
  );
};

const styles = StyleSheet.create({
  base: {
    backgroundColor: CCColors.White,
    borderRadius: BorderRadius.large,
    borderWidth: 1,
    borderColor: updateColorWithOpacity(CCColors.TextColor.Dark, 0.08),
  },
  elevated: {
    shadowColor: CCColors.TextColor.Dark,
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 6,
    elevation: 3,
  },
});

export default Card;
